interface RoleDefinition {
  readonly value: string;
  readonly label: string;
  readonly userAssignable: boolean;
}

/**
 * Canonical role definitions shared by frontend and backend consumers.
 */
export const ROLE_DEFINITIONS = {
  ProjectReader: { value: "project-reader", label: "Project Reader", userAssignable: true },
  ProjectWriter: { value: "project-writer", label: "Project Writer", userAssignable: true },
  ProjectTechnical: { value: "project-technical", label: "Project Technical", userAssignable: true },
  Decision: { value: "decision", label: "Decision", userAssignable: true },
  ReleaseManager: { value: "release-manager", label: "Release Manager", userAssignable: true },
  ReportViewer: { value: "report-viewer", label: "Report Viewer", userAssignable: true },
  Tester: { value: "tester", label: "Tester", userAssignable: true },
  ContactsReadAll: { value: "contacts-read-all", label: "Contacts Read All", userAssignable: true },
  ContactsWriter: { value: "contacts-writer", label: "Contacts Writer", userAssignable: true },
  TaskReadAll: { value: "task-read-all", label: "Task Read All", userAssignable: true },
  TaskRead: { value: "task-read", label: "Task Read", userAssignable: true },
  BotReader: { value: "bot-reader", label: "Bot Reader", userAssignable: true },
  BotWriter: { value: "bot-writer", label: "Bot Writer", userAssignable: true },
  FileReader: { value: "file-reader", label: "File Reader", userAssignable: true },
  Owner: { value: "owner", label: "Owner", userAssignable: true },
  SuperAdmin: { value: "superadmin", label: "Super Admin", userAssignable: true },
  TimesheetReader: { value: "timesheet-reader", label: "Timesheet Reader", userAssignable: true },
  TimesheetReaderAll: { value: "timesheet-reader-all", label: "Timesheet Reader All", userAssignable: true },
  TimesheetAuditor: { value: "timesheet-auditor", label: "Timesheet Auditor", userAssignable: true },
  TimesheetPayroll: { value: "timesheet-payroll", label: "Timesheet Payroll", userAssignable: true },
  TimesheetWriter: { value: "timesheet-writer", label: "Timesheet Writer", userAssignable: true },
  TimesheetWriterAll: { value: "timesheet-writer-all", label: "Timesheet Writer All", userAssignable: true },
  TimesheetManager: { value: "timesheet-manager", label: "Timesheet Manager", userAssignable: true },
  TimesheetPlannerWriter: {
    value: "timesheet-work-planner-writer",
    label: "Timesheet Work Planner Writer",
    userAssignable: true,
  },
  TimesheetPlannerReader: {
    value: "timesheet-work-planner-reader",
    label: "Timesheet Work Planner Reader",
    userAssignable: true,
  },
  TimesheetAnalytics: { value: "timesheet-analytics", label: "Timesheet Analytics", userAssignable: true },
  TimesheetSystem: { value: "timesheet-system", label: "Timesheet System", userAssignable: true },
  TimesheetSupport: { value: "timesheet-support", label: "Timesheet Support", userAssignable: true },
  TimesheetAdmin: { value: "timesheet-admin", label: "Timesheet Admin", userAssignable: true },
  System: { value: "system", label: "System", userAssignable: false },
} as const satisfies Record<string, RoleDefinition>;

/** The stable property names used to access role values. */
export type RoleName = keyof typeof ROLE_DEFINITIONS;

/** A role value accepted by the platform. */
export type Role = (typeof ROLE_DEFINITIONS)[RoleName]["value"];

/** A role and its human-readable label. */
export interface RoleOption {
  readonly value: Role;
  readonly label: string;
}

const roleDefinitions = Object.values(ROLE_DEFINITIONS);

/** Role values keyed by their stable property names. */
export const Roles = Object.fromEntries(
  Object.entries(ROLE_DEFINITIONS).map(([name, definition]) => [name, definition.value]),
) as { readonly [Name in RoleName]: (typeof ROLE_DEFINITIONS)[Name]["value"] };

/** Every role value supported by the platform. */
export const ALL_ROLES = roleDefinitions.map(({ value }) => value) as Role[];

/** Roles that can be assigned to an interactive user. */
export const USER_ROLES = roleDefinitions
  .filter(({ userAssignable }) => userAssignable)
  .map(({ value }) => value) as Role[];

/** User-assignable role values and labels. */
export const USER_ROLE_OPTIONS = roleDefinitions
  .filter(({ userAssignable }) => userAssignable)
  .map(({ value, label }) => ({ value, label })) as RoleOption[];

/** API-key role values and labels, including key-only roles. */
export const API_KEY_ROLE_OPTIONS = roleDefinitions.map(({ value, label }) => ({ value, label })) as RoleOption[];
